/**
 * quoteRouting — shared route resolver for quote navigation.
 *
 * DEC-44/DEF-106: one function, multiple call sites (QuotesPage row click,
 * R&S Back/Cancel, RecordDecisionPage, CustomerDetail quotes tab).
 *
 * Rule: converted (any status) → its job; active/draft/unsent → builder
 * (/edit); sent and won-without-a-job → builder (/edit), which opens them
 * locked (IDI-132 / D-01: no separate view); everything else (lost, expired,
 * archived) → detail.
 *
 * IDI-157 (BUILD CONTRACT 2026-10-21): "converted" = convertedJobId /
 * converted_job_id, whatever the status — lost-but-converted included.
 */

/**
 * The job id a quote was converted into, or null when it was never converted.
 * Only the conversion keys count — job_id/jobId are not a conversion signal.
 */
export function convertedJobIdOf(quote) {
  if (!quote) return null;
  for (const raw of [quote.convertedJobId, quote.converted_job_id]) {
    if (raw === undefined || raw === null) continue;
    const s = String(raw).trim();
    if (s) return s;
  }
  return null;
}

/**
 * Given a quote object, return the correct route path.
 * @param {object} quote - Quote object with at least { id, status }
 * @returns {string} Route path
 */
export function getQuoteRoute(quote) {
  if (!quote || !quote.id) return '/quotes';

  // IDI-157: a converted quote is a job — any status.
  const convertedJobId = convertedJobIdOf(quote);
  if (convertedJobId) return `/jobs/${convertedJobId}`;

  const s = (quote.status || '').toLowerCase();

  // Editable statuses → builder
  if (s === 'draft' || s === 'active' || s === 'not_sent' || s === 'unsent') {
    return `/quotes/${quote.id}/edit`;
  }

  // IDI-132 (D-01): a sent quote opens as the full quote, locked.
  if (s === 'sent') return `/quotes/${quote.id}/edit`;

  // Won/accepted → linked job if available; else the locked builder.
  if (s === 'won' || s === 'accepted') {
    const jobId = quote.job_id || quote.jobId;
    if (jobId) return `/jobs/${jobId}`;
    return `/quotes/${quote.id}/edit`;
  }

  // Everything else → read-only detail
  return `/quotes/${quote.id}`;
}
